// TMOS Provisioning Profiles Management Page
// CRM-style layout: four top-level segment tabs (Individual, Tenant, Consulting, PE),
// each with three sub-tabs — Tenant (tenant-scoped), Tenant admin (user-scoped on admin profile), User (general user profile).

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  provisioningProfileService,
  ProvisioningProfile,
  TemplateType,
  ProvisionScope,
  ProvisioningProfileRole,
} from '@/services/provisioningProfileService';
import { toast } from 'react-hot-toast';
import {
  ServerIcon,
  TrashIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/Badge';
import { TemplateSelectionModal } from '@/components/provisioning/TemplateSelectionModal';
import { RoleSelectionModal } from '@/components/provisioning/RoleSelectionModal';
import { ProvisioningTemplateTypeIconBar } from '@/components/provisioning/ProvisioningTemplateTypeIconBar';

type GroupType = 'Individual' | 'Tenant' | 'Consulting' | 'PE';
/** Tenant = TENANT-scoped rows on admin profile; tenantAdmin = USER-scoped rows on admin profile; user = full user profile */
type RoleTab = 'tenant' | 'tenantAdmin' | 'user';

const PROFILE_GROUP_DEFS: Array<{
  type: GroupType;
  icon: React.ElementType;
  adminProfileCode: string | null;
  userProfileCode: string | null;
}> = [
  {
    type: 'Individual',
    icon: UserCircleIcon,
    adminProfileCode: null,
    userProfileCode: 'INDIVIDUAL_USER',
  },
  {
    type: 'Tenant',
    icon: BuildingOfficeIcon,
    adminProfileCode: 'TENANT_ADMIN',
    userProfileCode: 'TENANT_USER',
  },
  {
    type: 'Consulting',
    icon: BriefcaseIcon,
    adminProfileCode: 'CONSULTANT_ADMIN',
    userProfileCode: 'CONSULTANT_USER',
  },
  {
    type: 'PE',
    icon: ChartBarIcon,
    adminProfileCode: 'PE_ADMIN',
    userProfileCode: 'PE_USER',
  },
];

export const ProvisioningProfilesPage: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const [profiles, setProfiles] = useState<ProvisioningProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState<GroupType>('Individual');
  const [activeRoleTab, setActiveRoleTab] = useState<RoleTab>('user');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType | null>(null);
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  const profileGroups = useMemo(
    () =>
      PROFILE_GROUP_DEFS.map((g) => ({
        ...g,
        label:
          g.type === 'Individual'
            ? t('provisioningProfiles.segment.individual')
            : g.type === 'Tenant'
              ? t('provisioningProfiles.segment.tenant')
              : g.type === 'Consulting'
                ? t('provisioningProfiles.segment.consulting')
                : t('provisioningProfiles.segment.pe'),
        shortHint:
          g.type === 'Individual'
            ? t('provisioningProfiles.segmentHint.individual')
            : g.type === 'Tenant'
              ? t('provisioningProfiles.segmentHint.tenant')
              : g.type === 'Consulting'
                ? t('provisioningProfiles.segmentHint.consulting')
                : t('provisioningProfiles.segmentHint.pe'),
      })),
    [t, currentLanguage.id]
  );

  const profileByCode = useMemo(() => {
    const map: Record<string, ProvisioningProfile> = {};
    profiles.forEach((p) => {
      map[p.profile_code] = p;
    });
    return map;
  }, [profiles]);

  const activeGroupDef = useMemo(
    () => profileGroups.find((g) => g.type === activeGroup) ?? profileGroups[0],
    [activeGroup, profileGroups]
  );

  const resolvedProfileCode = useMemo(() => {
    if (activeRoleTab === 'user') return activeGroupDef.userProfileCode;
    return activeGroupDef.adminProfileCode;
  }, [activeGroupDef, activeRoleTab]);

  const focusProfile: ProvisioningProfile | null = useMemo(() => {
    if (!resolvedProfileCode) return null;
    return profileByCode[resolvedProfileCode] ?? null;
  }, [profileByCode, resolvedProfileCode]);

  /** Templates + roles: Tenant tab = TENANT scope only; Tenant admin tab = USER scope only on admin profile; User tab = full user profile */
  const contentScopeFilter: 'ALL' | 'TENANT' | 'USER' = useMemo(() => {
    if (activeRoleTab === 'user') return 'ALL';
    if (activeRoleTab === 'tenant') return 'TENANT';
    return 'USER';
  }, [activeRoleTab]);

  useEffect(() => {
    void loadProfiles();
  }, []);

  const prevSegmentRef = useRef<GroupType>(activeGroup);

  /** Individual → only User. Leaving Individual → open Tenant sub-tab first. */
  useEffect(() => {
    const prev = prevSegmentRef.current;
    prevSegmentRef.current = activeGroup;
    if (activeGroup === 'Individual') {
      setActiveRoleTab('user');
    } else if (prev === 'Individual') {
      setActiveRoleTab('tenant');
    }
  }, [activeGroup]);

  const loadProfiles = async (): Promise<ProvisioningProfile[]> => {
    try {
      setLoading(true);
      const data = await provisioningProfileService.getAllProfiles();
      setProfiles(data);
      return data;
    } catch (error: unknown) {
      console.error('Error loading provisioning profiles:', error);
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(msg || t('provisioningProfiles.failedToLoad'));
      return [];
    } finally {
      setLoading(false);
    }
  };

  const refreshAfterMutation = useCallback(async () => {
    await loadProfiles();
  }, []);

  const handleRemoveTemplate = async (profileId: string, templateType: TemplateType, templateId: string) => {
    if (!confirm(t('provisioningProfiles.confirmRemove'))) return;
    try {
      await provisioningProfileService.removeTemplateAssociation(profileId, templateType, templateId);
      toast.success(t('provisioningProfiles.templateRemoved'));
      await refreshAfterMutation();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(msg || t('provisioningProfiles.failedToRemove'));
    }
  };

  const openTemplatePickerForType = (profileId: string, type: TemplateType) => {
    setCurrentProfileId(profileId);
    setSelectedTemplateType(type);
    setTemplateModalOpen(true);
  };

  const openRoleModal = (profileId: string) => {
    setCurrentProfileId(profileId);
    setRoleModalOpen(true);
  };

  const handleTemplateSelected = async (
    templateId: string,
    templateName: string,
    isRequired: boolean,
    provisionOrder: number,
    provisionScope: ProvisionScope
  ) => {
    if (!currentProfileId || !selectedTemplateType) return;
    try {
      await provisioningProfileService.associateTemplate({
        profileId: currentProfileId,
        templateType: selectedTemplateType,
        templateId,
        templateName,
        isRequired,
        provisionOrder,
        provisionScope,
      });
      toast.success(t('provisioningProfiles.templateAssociated'));
      await refreshAfterMutation();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(msg || t('provisioningProfiles.failedToAssociate'));
    }
  };

  const handleRoleSelected = async (roleId: string, provisionOrder: number, provisionScope: ProvisionScope) => {
    if (!currentProfileId) return;
    try {
      await provisioningProfileService.associateRole({
        profileId: currentProfileId,
        roleId,
        provisionOrder,
        provisionScope,
      });
      toast.success(t('provisioningProfiles.roleAssociated'));
      await refreshAfterMutation();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(msg || t('provisioningProfiles.failedToAssociateRole'));
    }
  };

  const handleRemoveRole = async (profileId: string, roleId: string) => {
    if (!confirm(t('provisioningProfiles.confirmRemoveRole'))) return;
    try {
      await provisioningProfileService.removeRoleAssociation(profileId, roleId);
      toast.success(t('provisioningProfiles.roleRemoved') || 'Role association removed');
      await refreshAfterMutation();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(msg || t('provisioningProfiles.failedToRemoveRole'));
    }
  };

  const renderProfileContent = (profile: ProvisioningProfile, scopeFilter: 'ALL' | 'TENANT' | 'USER') => {
    const displayProfile = profile;
    const allTemplates = displayProfile.templates || [];
    const tenantTemplates = allTemplates.filter((x) => (x.provision_scope || 'USER') === 'TENANT');
    const userTemplates = allTemplates.filter((x) => (x.provision_scope || 'USER') === 'USER');
    const filteredTemplates =
      scopeFilter === 'ALL' ? allTemplates : scopeFilter === 'TENANT' ? tenantTemplates : userTemplates;
    const allRoles: ProvisioningProfileRole[] = displayProfile.roles || [];
    const filteredRoles: ProvisioningProfileRole[] =
      scopeFilter === 'ALL'
        ? allRoles
        : allRoles.filter((r) => (r.provision_scope || 'USER') === (scopeFilter === 'TENANT' ? 'TENANT' : 'USER'));
    return (
      <div className="space-y-3">
        <div className="text-xs text-[var(--color-secondary-text)]">
          {t('provisioningProfiles.summaryTemplates', { count: filteredTemplates.length })}
          {scopeFilter !== 'ALL' && (
            <span className="ml-1">
              {scopeFilter === 'TENANT'
                ? t('provisioningProfiles.scopeSuffixTenant')
                : t('provisioningProfiles.scopeSuffixUser')}
            </span>
          )}
          {filteredRoles.length > 0 && (
            <span className="ml-2">{t('provisioningProfiles.summaryRoles', { count: filteredRoles.length })}</span>
          )}
        </div>

        <ProvisioningTemplateTypeIconBar
          onSelectTemplateType={(type) => openTemplatePickerForType(profile.id, type)}
          onAddRoles={() => openRoleModal(profile.id)}
          disabled={loading}
        />

        {filteredTemplates.length > 0 ? (
          <div className="space-y-2">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-start justify-between p-2 bg-[var(--color-secondary-bg)] rounded border border-[var(--color-border)] text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap mb-1">
                    <span className="font-medium text-[var(--color-surface-text)] truncate">
                      {template.template_name || template.template_id}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {template.template_type}
                    </Badge>
                    <Badge variant={template.provision_scope === 'TENANT' ? 'success' : 'info'} className="text-xs">
                      {template.provision_scope === 'TENANT'
                        ? t('provisioningProfiles.tenant')
                        : t('provisioningProfiles.user')}
                    </Badge>
                    {template.is_required && (
                      <Badge variant="warning" className="text-xs">
                        {t('provisioningProfiles.required') || 'Required'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[var(--color-secondary-text)]">
                    {t('provisioningProfiles.orderLabel')} {template.provision_order}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveTemplate(profile.id, template.template_type, template.template_id)}
                  className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-error)] ml-2 flex-shrink-0"
                  title={t('provisioningProfiles.removeTemplate')}
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--color-secondary-text)] text-center py-2">
            {t('provisioningProfiles.noTemplatesAssociated')}
          </p>
        )}

        <div className="border-t border-[var(--color-border)] pt-3">
          <h4 className="text-xs font-semibold text-[var(--color-surface-text)] mb-2">
            {t('provisioningProfiles.associatedRoles')}
          </h4>
          {filteredRoles.length > 0 ? (
            <div className="space-y-2">
              {filteredRoles.map((role: ProvisioningProfileRole) => (
                <div
                  key={role.id}
                  className="flex items-start justify-between p-2 bg-[var(--color-secondary-bg)] rounded border border-[var(--color-border)] text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-medium text-[var(--color-surface-text)]">
                        {role.display_name || role.role_name || role.role_id}
                      </span>
                      <Badge variant={role.provision_scope === 'TENANT' ? 'success' : 'info'} className="text-xs">
                        {role.provision_scope === 'TENANT'
                          ? t('provisioningProfiles.tenant')
                          : t('provisioningProfiles.user')}
                      </Badge>
                    </div>
                    <p className="text-[var(--color-secondary-text)]">
                      {t('provisioningProfiles.orderLabel')} {role.provision_order}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveRole(profile.id, role.role_id)}
                    className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-error)] ml-2 flex-shrink-0"
                    title={t('provisioningProfiles.removeRole')}
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--color-secondary-text)] text-center py-2">
              {scopeFilter === 'ALL'
                ? t('provisioningProfiles.noRolesAssociated')
                : scopeFilter === 'TENANT'
                  ? t('provisioningProfiles.noRolesInViewTenant')
                  : t('provisioningProfiles.noRolesInViewUser')}
            </p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-[var(--color-secondary-text)]">{t('provisioningProfiles.loading')}</p>
        </div>
      </div>
    );
  }

  const tenantTabLabel = t('provisioningProfiles.tabTenant');
  const tenantAdminLabel = t('provisioningProfiles.tabTenantAdmin');
  const userLabel = t('provisioningProfiles.tabUser');

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <ServerIcon className="w-6 h-6 text-[var(--color-surface-text)] shrink-0" />
                <div>
                  <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                    {t('provisioningProfiles.title')}
                  </h1>
                  <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
                    {t('provisioningProfiles.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-[var(--color-info)]/10 border border-[var(--color-info)]/20 rounded-lg">
            <h3 className="text-xs font-semibold text-[var(--color-info)] mb-1">
              {t('provisioningProfiles.scopeExplained')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-[var(--color-info)]">
              <div>
                <span className="font-medium">{t('provisioningProfiles.scope.tenantTitle')}</span>
                <p className="mt-1">{t('provisioningProfiles.scope.tenantBody')}</p>
              </div>
              <div>
                <span className="font-medium">{t('provisioningProfiles.scope.userTitle')}</span>
                <p className="mt-1">{t('provisioningProfiles.scope.userBody')}</p>
              </div>
              <div>
                <span className="font-medium">{t('provisioningProfiles.scope.adminTitle')}</span>
                <p className="mt-1">{t('provisioningProfiles.scope.adminBody')}</p>
              </div>
            </div>
          </div>

          {/* Segment tabs: Individual | Tenant | Consulting | PE */}
          <div className="mt-6">
            <div className="tab-bar">
              <nav className="flex gap-1 overflow-x-auto items-end" aria-label={t('provisioningProfiles.ariaProvisioningSegment')}>
                {profileGroups.map((g) => {
                  const Icon = g.icon;
                  const isActive = activeGroup === g.type;
                  return (
                    <button
                      key={g.type}
                      type="button"
                      onClick={() => setActiveGroup(g.type)}
                      className={`tab-button inline-flex items-center gap-2 ${isActive ? 'tab-button-selected' : ''}`}
                      aria-selected={isActive}
                    >
                      <Icon className="h-5 w-5 shrink-0 [stroke:currentColor]" />
                      <span className="truncate max-w-[12rem]">{g.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
            <p className="mt-2 text-xs text-[var(--color-secondary-text)]">{activeGroupDef.shortHint}</p>
          </div>

          {/* Sub-tabs: Tenant (tenant scope) | Tenant admin (user scope on admin profile) | User */}
          <div className="mt-4">
            <div className="tab-bar">
              <nav className="flex gap-1 overflow-x-auto items-end" aria-label={t('provisioningProfiles.ariaSubTabs')}>
                <button
                  type="button"
                  onClick={() => setActiveRoleTab('tenant')}
                  className={`tab-button inline-flex items-center gap-2 ${activeRoleTab === 'tenant' ? 'tab-button-selected' : ''}`}
                  aria-selected={activeRoleTab === 'tenant'}
                  disabled={activeGroup === 'Individual'}
                  title={
                    activeGroup === 'Individual'
                      ? t('provisioningProfiles.ariaTenantTabDisabled')
                      : t('provisioningProfiles.ariaTenantTab')
                  }
                >
                  {tenantTabLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveRoleTab('tenantAdmin')}
                  className={`tab-button inline-flex items-center gap-2 ${activeRoleTab === 'tenantAdmin' ? 'tab-button-selected' : ''}`}
                  aria-selected={activeRoleTab === 'tenantAdmin'}
                  disabled={activeGroup === 'Individual'}
                  title={
                    activeGroup === 'Individual'
                      ? t('provisioningProfiles.ariaTenantAdminDisabled')
                      : t('provisioningProfiles.ariaTenantAdmin')
                  }
                >
                  {tenantAdminLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveRoleTab('user')}
                  className={`tab-button inline-flex items-center gap-2 ${activeRoleTab === 'user' ? 'tab-button-selected' : ''}`}
                  aria-selected={activeRoleTab === 'user'}
                >
                  {userLabel}
                </button>
              </nav>
            </div>
            {activeGroup !== 'Individual' && (
              <p className="mt-2 text-xs text-[var(--color-secondary-text)]">
                {activeRoleTab === 'tenant' && t('provisioningProfiles.tabHelpTenant')}
                {activeRoleTab === 'tenantAdmin' && t('provisioningProfiles.tabHelpTenantAdmin')}
                {activeRoleTab === 'user' && t('provisioningProfiles.tabHelpUser')}
              </p>
            )}
          </div>

          <div className="mt-6">
            {activeGroup === 'Individual' && activeRoleTab !== 'user' ? (
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('provisioningProfiles.individualOnlyUserHint')
                  .split('{{tab}}')
                  .map((part, i, arr) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < arr.length - 1 && (
                        <strong className="text-[var(--color-surface-text)]">{userLabel}</strong>
                      )}
                    </React.Fragment>
                  ))}
              </p>
            ) : focusProfile ? (
              renderProfileContent(focusProfile, contentScopeFilter)
            ) : (
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('provisioningProfiles.profileNotFound', { code: resolvedProfileCode ?? '—' })}
              </p>
            )}
          </div>
        </div>
      </div>

      {currentProfileId && selectedTemplateType && (
        <TemplateSelectionModal
          isOpen={templateModalOpen}
          onClose={() => {
            setTemplateModalOpen(false);
            setSelectedTemplateType(null);
            setCurrentProfileId(null);
          }}
          profileId={currentProfileId}
          templateType={selectedTemplateType}
          onSelect={handleTemplateSelected}
          existingTemplateIds={focusProfile?.templates?.map((x) => x.template_id) || []}
        />
      )}

      {currentProfileId && (
        <RoleSelectionModal
          isOpen={roleModalOpen}
          onClose={() => {
            setRoleModalOpen(false);
            setCurrentProfileId(null);
          }}
          profileId={currentProfileId}
          onSelect={handleRoleSelected}
          existingRoleIds={focusProfile?.roles?.map((r: ProvisioningProfileRole) => r.role_id) || []}
        />
      )}
    </div>
  );
};
